import type { SupplierCollectionPoint } from '../types/supplier.type';
import { isDemoMode } from '../demo/core';

const EARTH_RADIUS_KM = 6371;
const AVERAGE_SPEED_KMH = 35;

export function haversineDistanceKm(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

type NullableLatLng = { latitude: number | null; longitude: number | null } | null | undefined;

function asLatLng(location: NullableLatLng): { latitude: number; longitude: number } | null {
  return location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude)
    ? { latitude: location.latitude as number, longitude: location.longitude as number }
    : null;
}

/**
 * Straight-line distance/ETA fallback for when road-routing hasn't resolved yet.
 * Optionally anchored at a start/end location (e.g. a route's start/end facility)
 * so the estimate reflects the full trip, not just stop-to-stop.
 */
export function computeRouteMetrics(
  points: SupplierCollectionPoint[],
  startLocation?: NullableLatLng,
  endLocation?: NullableLatLng,
): { distanceKm: number; etaMinutes: number } {
  const start = asLatLng(startLocation);
  const end = asLatLng(endLocation);
  const chain = [...(start ? [start] : []), ...points, ...(end ? [end] : [])];
  let distanceKm = 0;
  for (let i = 1; i < chain.length; i += 1) {
    distanceKm += haversineDistanceKm(chain[i - 1], chain[i]);
  }
  return { distanceKm: Math.round(distanceKm * 10) / 10, etaMinutes: Math.round((distanceKm / AVERAGE_SPEED_KMH) * 60) };
}

export function formatEta(etaMinutes: number): string {
  const hours = Math.floor(etaMinutes / 60);
  const minutes = etaMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

const OSRM_TRIP_BASE_URL = 'https://router.project-osrm.org/trip/v1/driving';

/**
 * Reorders the given stops to (approximately) minimize total road travel distance/time.
 * When a start/end facility location is given, it's anchored as the fixed first/last leg
 * of the trip (the stops in between are the only thing reordered) so the optimization
 * reflects the full journey, not just stop-to-stop. Without a facility, the currently-first
 * stop stays fixed as the start, matching prior behavior. Delegates to OSRM's Trip service
 * (the same free, keyless OSRM demo server already used for road-following route geometry),
 * which solves this against the real road network rather than straight-line distance. Returns
 * null if optimization isn't possible (fewer than 3 located stops) or the service call fails -
 * callers should keep the existing order in that case.
 */
export async function optimizeStopOrder(
  points: SupplierCollectionPoint[],
  startLocation?: NullableLatLng,
  endLocation?: NullableLatLng,
): Promise<string[] | null> {
  const located = points.filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
  if (located.length < 3) return null;

  const START_ID = '__route_start_facility__';
  const END_ID = '__route_end_facility__';
  const start = asLatLng(startLocation);
  const end = asLatLng(endLocation);
  const chain: { id: string; latitude: number; longitude: number }[] = [
    ...(start ? [{ id: START_ID, ...start }] : []),
    ...located,
    ...(end ? [{ id: END_ID, ...end }] : []),
  ];

  // Demo: no external routing service; order the stops greedily by straight-line distance.
  if (isDemoMode()) return nearestNeighbourOrder(located, start);

  const coords = chain.map((point) => `${point.longitude},${point.latitude}`).join(';');
  const destinationParam = end ? '&destination=last' : '';
  try {
    const response = await fetch(`${OSRM_TRIP_BASE_URL}/${coords}?source=first${destinationParam}&roundtrip=false`);
    const data = await response.json();
    const waypoints = data?.waypoints as { waypoint_index: number }[] | undefined;
    if (!waypoints || waypoints.length !== chain.length) return null;
    return chain
      .map((point, index) => ({ point, order: waypoints[index].waypoint_index }))
      .filter((entry) => entry.point.id !== START_ID && entry.point.id !== END_ID)
      .sort((a, b) => a.order - b.order)
      .map((entry) => entry.point.id);
  } catch {
    return null;
  }
}

/** Greedy nearest-neighbour stop order from the start (or the first stop) -- the demo's offline stand-in for OSRM Trip. */
function nearestNeighbourOrder(
  points: SupplierCollectionPoint[],
  start: { latitude: number; longitude: number } | null,
): string[] {
  const remaining = [...points];
  const order: string[] = [];
  let current: { latitude: number; longitude: number } = start ?? remaining[0];
  if (!start) order.push(remaining.shift()!.id);
  while (remaining.length) {
    let best = 0;
    for (let i = 1; i < remaining.length; i += 1) {
      if (haversineDistanceKm(current, remaining[i]) < haversineDistanceKm(current, remaining[best])) best = i;
    }
    const [next] = remaining.splice(best, 1);
    order.push(next.id);
    current = next;
  }
  return order;
}
